import { hoursFixed } from '#shared/duration'
import type { WeekGrid as Grid } from '#shared/reports'

interface Props {
  /** The seven dates, Monday first. */
  days: string[]
  grid: Grid
  /** Today in the viewer's timezone, so the current column is marked. */
  today: string
  onPrev: () => void
  onNext: () => void
  /** False on the current week: there is nothing to log in the future. */
  canGoForward: boolean
  /** Omitted for a grid whose cells do nothing. */
  onCell?: (projectId: string, date: string) => void
  /**
   * What clicking a cell is for.
   *
   * 'log' opens the cell to be filled in, so an empty cell is the most
   * important one to be able to click. 'view' only shows what is already
   * there, so an empty cell has nothing to open and is left inert rather
   * than offering a dialog that would say "nothing logged here".
   */
  cellAction?: 'log' | 'view'
}

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `21-Sep-2026`, parsed off the string rather than through a Date. */
function longDate(date: string): string {
  const [year, month, day] = date.split('-')
  return `${day}-${MONTHS[Number(month) - 1]}-${year}`
}

/** `21` — the day of the month, without a leading zero. */
function dayNumber(date: string): number {
  return Number(date.slice(8))
}

/** `Tuesday 22 September`, for the screen-reader name on a cell. */
function spokenDate(date: string, index: number): string {
  const [, month] = date.split('-')
  const long = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
  return `${long[index]} ${dayNumber(date)} ${MONTHS[Number(month) - 1]}`
}

/**
 * A week as a project-by-day table.
 *
 * This replaced a tab bar showing one day at a time, and the reason is the
 * question people actually ask a timesheet: not "what did I do on Tuesday"
 * but "where did the week go, and what have I not filled in yet". A tab bar
 * can only answer the first, because it shows one column and hides six.
 *
 * Only projects with time in the week get a row, so the table is as short as
 * the week was. An empty day cell is left blank rather than printed as 0.00:
 * seven zeroes across a row is noise, and the gaps are the thing worth
 * seeing. Totals always carry two decimals, so the column reads straight
 * down instead of the decimal point moving row to row.
 */
export function WeekGrid({
  days,
  grid,
  today,
  onPrev,
  onNext,
  canGoForward,
  onCell,
  cellAction = 'log',
}: Props) {
  const todayIndex = days.indexOf(today)

  /** Whether this cell has anything to click. */
  const clickable = (minutes: number) =>
    Boolean(onCell) && (cellAction === 'log' || minutes > 0)

  function cellLabel(name: string, minutes: number, date: string, index: number): string {
    const when = `${name}, ${spokenDate(date, index)}`
    if (!minutes) return `Log time on ${when}`
    return cellAction === 'log'
      ? `${hoursFixed(minutes)} hours on ${when} — edit`
      : `${hoursFixed(minutes)} hours on ${when} — show entries`
  }

  return (
    <div className="week-grid">
      <div className="week-nav">
        <button type="button" className="btn-ghost" onClick={onPrev}>
          ← Prev week
        </button>
        <span className="week-range">
          {longDate(days[0])} – {longDate(days[6])}
        </span>
        <button type="button" className="btn-ghost" onClick={onNext} disabled={!canGoForward}>
          Next week →
        </button>
      </div>

      {grid.rows.length === 0 ? (
        <p className="empty">Nothing logged this week.</p>
      ) : (
        <div className="week-grid-scroll">
          <table className="week-table">
            <caption className="sr-only">
              Hours logged per project for each day of the week of {longDate(days[0])}
            </caption>
            <thead>
              <tr>
                <th scope="col" className="week-table-project">
                  Project
                </th>
                {days.map((date, index) => (
                  <th
                    key={date}
                    scope="col"
                    className={`num${index === todayIndex ? ' is-today' : ''}`}
                  >
                    {dayNumber(date)} {WEEKDAYS[index]}
                  </th>
                ))}
                <th scope="col" className="num week-table-total">
                  Total
                </th>
              </tr>
            </thead>

            <tbody>
              {grid.rows.map((row) => (
                <tr key={row.projectId}>
                  <th scope="row" className="week-table-project">
                    {row.name}
                  </th>

                  {row.minutes.map((minutes, index) => (
                    <td
                      key={days[index]}
                      className={`num week-cell${index === todayIndex ? ' is-today' : ''}`}
                    >
                      {clickable(minutes) ? (
                        <button
                          type="button"
                          className="week-cell-btn"
                          onClick={() => onCell?.(row.projectId, days[index])}
                          // The visible text is a bare number, or nothing at
                          // all in an empty cell, so the whole question the
                          // button answers has to be in its name.
                          aria-label={cellLabel(row.name, minutes, days[index], index)}
                        >
                          {minutes ? hoursFixed(minutes) : ''}
                        </button>
                      ) : (
                        <span className="week-cell-flat">
                          {minutes ? hoursFixed(minutes) : ''}
                        </span>
                      )}
                    </td>
                  ))}

                  <td className="num week-table-total">{hoursFixed(row.total)}</td>
                </tr>
              ))}
            </tbody>

            {/* A footer rather than another row: these are the columns added
                up, not another project, and a screen reader should not read
                them as one. */}
            <tfoot>
              <tr>
                <th scope="row" className="week-table-project">
                  Total
                </th>
                {grid.dayTotals.map((minutes, index) => (
                  <td
                    key={days[index]}
                    className={`num${index === todayIndex ? ' is-today' : ''}`}
                  >
                    {minutes ? hoursFixed(minutes) : ''}
                  </td>
                ))}
                <td className="num week-table-total">{hoursFixed(grid.total)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      )}
    </div>
  )
}
